import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, Menu, X } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EnquireButton } from "@/components/EnquireButton";

const NAV_LINKS = [
  { to: "/dashboard", label: "Dashboard" },
  { to: "/courses", label: "Courses" },
  { to: "/my-courses", label: "My Courses" },
  { to: "/subjects", label: "Subjects" },
  { to: "/quiz/new", label: "Practice" },
  { to: "/analytics", label: "Analytics" },
  { to: "/history", label: "History" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, status, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border/70 bg-white/80 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-logo-link">
          <BrandLogo className="h-9" />
        </Link>

        {status === "authenticated" && (
          <nav className="hidden items-center gap-1 lg:flex">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `rounded-full px-4 py-2 text-sm font-medium transition ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground/70 hover:bg-secondary hover:text-foreground"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {user?.role === "admin" && (
              <NavLink
                to="/admin"
                data-testid="nav-admin"
                className={({ isActive }) =>
                  `rounded-full px-4 py-2 text-sm font-semibold transition ${
                    isActive
                      ? "bg-[hsl(var(--brand-orange))] text-white"
                      : "text-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/10"
                  }`
                }
              >
                Admin
              </NavLink>
            )}
          </nav>
        )}

        {status === "guest" && (
          <nav className="hidden items-center gap-1 md:flex">
            <NavLink to="/courses" data-testid="nav-guest-courses" className="rounded-full px-4 py-2 text-sm font-medium text-foreground/70 hover:bg-secondary">Courses</NavLink>
            <NavLink to="/contact" data-testid="nav-guest-contact" className="rounded-full px-4 py-2 text-sm font-medium text-foreground/70 hover:bg-secondary">Contact</NavLink>
          </nav>
        )}

        <div className="flex items-center gap-2">
          <EnquireButton size="sm" className="hidden border-[hsl(var(--brand-orange))] text-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/10 xl:inline-flex" testId="nav-enquire-btn" />
          {status === "guest" && (
            <>
              <Button variant="ghost" onClick={() => navigate("/login")} data-testid="nav-login-btn">
                Log in
              </Button>
              <Button
                className="bg-[hsl(var(--brand-orange))] text-white hover:bg-[hsl(var(--brand-orange))]/90"
                onClick={() => navigate("/register")}
                data-testid="nav-register-btn"
              >
                Get Started
              </Button>
            </>
          )}
          {status === "authenticated" && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1.5 text-sm font-medium hover:bg-secondary"
                >
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground">
                    {user?.name?.[0]?.toUpperCase() || "U"}
                  </div>
                  <span className="hidden sm:block">{user?.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm font-semibold">{user?.name}</div>
                  <div className="text-xs text-muted-foreground">{user?.email}</div>
                  <div className="mt-1 inline-flex rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                    {user?.role}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                  Profile & password
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                  Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/history")} data-testid="menu-history">
                  Quiz History
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/bookmarks")} data-testid="menu-bookmarks">
                  Bookmarks
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut className="mr-2 h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <button
            className="rounded-md p-2 lg:hidden"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && status === "authenticated" && (
        <div className="border-t border-border bg-white px-4 py-3 lg:hidden">
          <div className="grid gap-1">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-2 text-sm font-medium hover:bg-secondary"
              >
                {l.label}
              </Link>
            ))}
            {user?.role === "admin" && (
              <Link to="/admin" onClick={() => setOpen(false)} className="rounded-md px-3 py-2 text-sm font-semibold text-[hsl(var(--brand-orange))]">
                Admin
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
